import { MESSAGE_TYPES } from '../lib/message-protocol';
import type {
  AddAccountWithTokenFailedPayload,
  AddAccountWithTokenOkPayload,
  AddAccountWithTokenPayload,
} from '../lib/message-protocol';
import type { PostMessageClient } from '../lib/client';

export class MultiAccountAutoAddError extends Error {
  constructor(public readonly reason: string, message?: string) {
    super(message ?? `multi-account auto-add failed: ${reason}`);
    this.name = 'MultiAccountAutoAddError';
  }
}

export interface SwitcherApi {
  addAccount: () => Promise<void>;
  switchAccount: (uuid: string) => Promise<void>;
}

export interface MultiAccountAutoAddOptions {
  accountUuid: string;
  sessionToken: string;
  switcher: SwitcherApi;
  /** The live PostMessageClient (typically from AccountSwitcherProvider context). */
  client: Pick<PostMessageClient, 'on' | 'off'>;
  /**
   * Function that posts a typed message to the iframe. Defaults to the
   * private `sendMessage` of PostMessageClient — injected here for unit
   * testability since the real method is private.
   */
  sendMessage: (type: string, payload: unknown) => void;
  /** Override the default 5000ms ack timeout. */
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 5_000;

/**
 * Push a pre-authenticated account into the destination's account switcher.
 *
 * 1. Sends ADD_ACCOUNT_WITH_TOKEN over postMessage
 * 2. Awaits ADD_ACCOUNT_WITH_TOKEN_OK / _FAILED (timeout-guarded)
 * 3. On OK, calls switcher.switchAccount(accountUuid)
 * 4. On FAILED or timeout, throws MultiAccountAutoAddError
 *
 * Used by HandoffLanding (F5). The iframe-side handler is the A6 work.
 */
export async function multiAccountAutoAdd(
  options: MultiAccountAutoAddOptions,
): Promise<void> {
  const { accountUuid, sessionToken, switcher, client, sendMessage } = options;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const payload: AddAccountWithTokenPayload = { accountUuid, sessionToken };

  await new Promise<void>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      client.off(MESSAGE_TYPES.ADD_ACCOUNT_WITH_TOKEN_OK, onOk);
      client.off(MESSAGE_TYPES.ADD_ACCOUNT_WITH_TOKEN_FAILED, onFailed);
      if (timer) clearTimeout(timer);
    };

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const onOk = (msg: { payload?: AddAccountWithTokenOkPayload }) => {
      const ack = msg?.payload;
      if (!ack || ack.accountUuid !== accountUuid) {
        settle(() => reject(new MultiAccountAutoAddError(
          'mismatch',
          `ack uuid ${ack?.accountUuid ?? 'undefined'} != requested ${accountUuid}`,
        )));
        return;
      }
      settle(() => resolve());
    };

    const onFailed = (msg: { payload?: AddAccountWithTokenFailedPayload }) => {
      const fail = msg?.payload;
      settle(() => reject(new MultiAccountAutoAddError(
        fail?.reason ?? 'unknown',
        fail?.message,
      )));
    };

    client.on(MESSAGE_TYPES.ADD_ACCOUNT_WITH_TOKEN_OK, onOk);
    client.on(MESSAGE_TYPES.ADD_ACCOUNT_WITH_TOKEN_FAILED, onFailed);

    timer = setTimeout(() => {
      settle(() => reject(new MultiAccountAutoAddError(
        'timeout',
        `no ack within ${timeoutMs}ms`,
      )));
    }, timeoutMs);

    sendMessage(MESSAGE_TYPES.ADD_ACCOUNT_WITH_TOKEN, payload);
  });

  await switcher.switchAccount(accountUuid);
}
